// src/services/user.service.js
import api from './api';

export const userService = {
  /**
   * Get users with pagination and filters
   */
  getUsers: async ({ role, search, page = 1, limit = 10 } = {}) => {
    try {
      const response = await api.get('/users', {
        params: { role, search, page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Get a single user by ID
   */
  getUserById: async (id) => {
    if (!id) {
      throw new Error('User ID is required');
    }

    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Create a new user
   */
  createUser: async (userData) => {
    if (!userData) {
      throw new Error('User data is required');
    }

    // Validate required fields
    const requiredFields = ['username', 'email', 'password', 'role'];
    const missingFields = requiredFields.filter(field => !userData[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    try {
      // Send userData as-is - backend expects studentInfo/staffInfo structure
      const response = await api.post('/users', userData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Update user details
   */
  // In userService.js
updateUser: async (id, userData) => {
  if (!id) {
    throw new Error('User ID is required');
  }

  if (!userData) {
    throw new Error('Update data is required');
  }

  try {
    const formattedData = {
      ...userData,
      profile: userData.profile ? {
        ...userData.profile,
        fullName: userData.fullName || '',
        phone: userData.phone || '',
      } : undefined
    };

    const response = await api.put(`/users/${id}`, formattedData);
    return response.data;

  } catch (error) {
    // THIS WAS THE PROBLEM - error was getting wrapped incorrectly
    console.error('Service error:', error);
    throw error;  // Just throw the original error
  }
},

  /**
   * Update user status (activate/deactivate)
   */
  updateUserStatus: async (id, isActive) => {
    if (!id) {
      throw new Error('User ID is required');
    }

    if (typeof isActive !== 'boolean') {
      throw new Error('Status must be a boolean value');
    }

    try {
      const response = await api.put(`/users/${id}/status`, { isActive });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Update user role
   */
  updateUserRole: async (id, role) => {
    if (!id) {
      throw new Error('User ID is required');
    }

    if (!role) {
      throw new Error('Role is required');
    }

    const validRoles = ['student', 'counselor', 'admin', 'receptionist', 'staff'];
    if (!validRoles.includes(role)) {
      throw new Error('Invalid role specified');
    }

    try {
      const response = await api.put(`/users/${id}/role`, { role });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Get list of counselors
   */
  getCounselors: async () => {
    try {
      const response = await api.get('/users/counselors');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Get list of receptionists
   */
  getReceptionists: async () => {
    try {
      const response = await api.get('/users/receptionists');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Search users
   */
  searchUsers: async (query = '', role = '') => {
    try {
      const response = await api.get('/users/search', {
        params: { query, role }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Export users
   */
  exportUsers: async ({ role, search } = {}) => {
    try {
      const response = await api.get('/users/export', {
        params: { role, search },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Delete user
   */
  deleteUser: async (id) => {
    if (!id) {
      throw new Error('User ID is required');
    }

    try {
      const response = await api.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  /**
   * Handle API errors consistently
   */
  handleError: (error) => {
    console.error('API Error:', error);
  
    // If it's a server response error with data
    if (error.response?.data) {
      // Check for validation errors array
      if (Array.isArray(error.response.data.errors)) {
        const validationErrors = error.response.data.errors
          .map(err => err.message || err)
          .join(', ');
        return new Error(validationErrors);
      }
      // Check for single error message
      if (error.response.data.message) {
        return new Error(error.response.data.message);
      }
      // Check for error string
      if (typeof error.response.data === 'string') {
        return new Error(error.response.data);
      }
    }
  
    // If the request was made but no response received
    if (error.request) {
      return new Error('No response received from server. Please check your connection.');
    }
  
    // If it's a client-side error with a message
    if (error.message) {
      return new Error(error.message);
    }
  
    // Default error
    return new Error('An unexpected error occurred. Please try again.');
  }
};

export default userService;